import React, { useState } from "react";

import {
  Mail,
  Phone,
  Building2,
  Calendar,
  Percent,
  Link,
  Folder,
  Users,
  Pencil
} from "lucide-react";

import Switch from "../../components/ui/Switch";

import BrochureSection from "../ui/BrochureSection";
import DomainItem from "../ui/DomainItem";
import InfoItem from "../ui/InfoItem";
import SectionCard from "../ui/SectionCard";

import { useAgentStore } from "../../store/agentStore";
import {useAuthStore} from "../../store/authStore";

import { toast } from "react-toastify";

import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import FormActions from "../ui/FormActions";


import ManageAgent from "../../pages/agent/ManageAgent";
import { formatDateDDMMYYYY } from "../../hooks/commonHooks";

import {
  useDomainsQuery,
  useCreateDomainMutation,
  useUpdateDomainMutation,
  useDeleteDomainMutation,
} from "../../queries/agents/useAgentsQuery";

export default function AgentBasicInfoSection() {
  const { selectedAgent, setSelectedAgent } = useAgentStore();

  const { role } = useAuthStore();
  const isAdmin = role === "admin";

  const [showEditAgentModal, setShowEditAgentModal] = useState(false);


  const agent = selectedAgent?.agent || null;

  const agentId = agent?.id;

  const { data: domainsData } = useDomainsQuery(
    isAdmin ? agentId : undefined
  );
  
  const domains = isAdmin
    ? domainsData || []
    : selectedAgent?.domains || [];

  const createDomainMutation = useCreateDomainMutation(agentId);
  const updateDomainMutation = useUpdateDomainMutation(agentId);
  const deleteDomainMutation = useDeleteDomainMutation(agentId);

  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [webhookInput, setWebhookInput] = useState("");

  const webhooks = domains ?? [];

  const isEditingWebhook = editingIndex !== null;

  if (!agent) {
    return null;
  }

  const handleAddWebhook = () => {
    setEditingIndex(null);
    setWebhookInput("");
    setShowWebhookModal(true);
  };

  const handleEditWebhook = (id) => {
    setEditingIndex(id);
    setWebhookInput(webhooks.find((webhook) => webhook?.id === id)?.domain);
    setShowWebhookModal(true);
  };

  const handleDeleteWebhook = (id) => {
    deleteDomainMutation.mutate(id);
  };

  const handleSaveWebhook = () => {
    if (!webhookInput) return;
    if (isEditingWebhook) {
      updateDomainMutation.mutate(
        {
          domain_id: editingIndex,
          data: { domain: webhookInput },
        },
        {
          onSuccess: () => {
            toast.success("Domain updated successfully");
            setSelectedAgent((prev) => {
              return {
                ...prev,
                domains: domains || [],
              };
            });
          },
        }
      );
    } else {
      createDomainMutation.mutate(
        { domain: webhookInput },
        {
          onSuccess: () => {
            toast.success("Domain created successfully");
            setSelectedAgent((prev) => ({
              ...prev,
              domains: [...(prev?.domains || []), webhookInput],
            }));
          },
        }
      );
    }
  
    setShowWebhookModal(false);
    setWebhookInput("");
    setEditingIndex(null);
  };
  const handleCancelWebhook = () => {
    setShowWebhookModal(false);
    setWebhookInput("");
    setEditingIndex(null);
  };

  const handleEditAgent = () => {
    setShowEditAgentModal(true);
  };

  return (
    <div className="agent-info-card ">

      {/* HEADER */}
      <header className="agent-header">
        <div>
          <h2 className="agent-title">{agent.name}</h2>
          <span className="agent-industry">
            <Building2 size={14} /> {agent.industry}
          </span>
        </div>
        {isAdmin && (
        <div className="d-flex gap-2 items-center agent-actions-container">
        <div className="agent-actions">
        <Button variant="secondary" onClick={handleEditAgent}>
          <Pencil size={18} className="text-white" />
        </Button>
      </div>
        <div className="agent-status">
          <Switch checked={agent.is_active} />

          <span
            className={`status-badge ${
              agent.is_active ? "active" : "inactive"
            }`}
          >
            {agent.is_active ? "Active" : "Inactive"}
          </span>
        </div>
        </div>
        )}
      </header>

      {/* BASIC INFO */}
      <div className="agent-grid">
        <InfoItem icon={Mail} label="Email">{agent.email}</InfoItem>
        <InfoItem icon={Phone} label="Mobile">{agent.mobile}</InfoItem>
        <InfoItem icon={Building2} label="Industry">{agent.industry}</InfoItem>
        <InfoItem icon={Calendar} label="Created">
          {formatDateDDMMYYYY(agent.created_at)}
        </InfoItem>
        <InfoItem icon={Percent} label="Budget Buffer 1">
          {agent.primary_budget_buffer_percent}%
        </InfoItem>
        <InfoItem icon={Percent} label="Budget Buffer 2">
          {agent.secondary_budget_buffer_percent}%
        </InfoItem>

        <InfoItem icon={Link} label="CRM Webhook">
          <a href={agent.crm_webhook_url} target="_blank">
            <span
            title={agent.crm_webhook_url}
            className="agent-webhook"
          >
            {agent.crm_webhook_url}
          </span>
          </a>
        </InfoItem>

        <InfoItem icon={Building2} label="About" full>
          {agent.about_us}
        </InfoItem>
      </div>

      {/* WEBHOOK */}

      <SectionCard
  title="Domains"
  className="webhook-section"
  icon={Link}
  actions={
      isAdmin && (
        <Button variant="secondary" onClick={handleAddWebhook}>
          + Add Domain
        </Button>
      )
  }
>
  {webhooks.map((webhook) => (
    <DomainItem
      key={webhook.id}
      domain={webhook}
      onEdit={() => handleEditWebhook(webhook.id)}
      onDelete={() => handleDeleteWebhook(webhook.id)}
    />
  ))}
</SectionCard>

{agent?.brochure_url && (
  <SectionCard title="Brochure" icon={Folder} className="brochure-section mt-4">
    <BrochureSection url={agent.brochure_url} title={agent.brochure_url?.split("/").pop()} className="brochure-section-content"/>
  </SectionCard>
)}


      {/* STATS */}
      <div className="agent-stats">
        <div className="stat-box">
          <Folder size={18} />
          <div>
            <h3>{selectedAgent?.properties_count}</h3>
            <p>Properties</p>
          </div>
        </div>

        <div className="stat-box">
          <Users size={18} />
          <div>
            <h3>{selectedAgent?.leads_count}</h3>
            <p>Total Leads</p>
          </div>
        </div>
      </div>
      <Modal
        isOpen={showEditAgentModal}
        onClose={() => setShowEditAgentModal(false)}
        title="Edit Agent"
        className="edit-agent-modal"
      >
        <ManageAgent onSuccess={() => setShowEditAgentModal(false)} onClose={() => setShowEditAgentModal(false)} />
      </Modal>
      <Modal
        isOpen={showWebhookModal}
        onClose={handleCancelWebhook}
        title={isEditingWebhook ? "Edit Domain" : "Add Domain"}
        className="webhook-add-modal"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveWebhook();
          }}
        >
          <Input
            label="Domain URL"
            value={webhookInput}
            onChange={(e) => setWebhookInput(e.target.value)}
            placeholder="Enter domain URL"
            className="webhook-input w-100"
          />

          <FormActions onCancel={handleCancelWebhook} saveType="submit" />
        </form>
      </Modal>
    </div>
  );
}
